import { useEffect, useState } from "react";
import api, { euro, pctIt } from "@/lib/api";
import { KpiCard, SectionTitle } from "@/components/common";
import { Wallet, TrendingUp, FileText, CheckCircle2, Clock } from "lucide-react";

const STATO_LABEL = { prevista: "Prevista", maturata: "Maturata", fatturata: "Fatturata", incassata: "Incassata" };
const Brk = ({ l, v, accent }) => (
  <div><div className="text-[10px] uppercase text-[var(--muted)]">{l}</div><div className={`mono text-sm font-bold ${accent ? "text-[#FC82ED]" : ""}`}>{euro(v || 0)}</div></div>
);
const STATO_CLS = {
  prevista: "text-slate-500 bg-slate-100 border-slate-300",
  maturata: "text-[#FC82ED] bg-[#FC82ED]/10 border-[#FC82ED]/30",
  fatturata: "text-amber-600 bg-amber-400/10 border-amber-400/30",
  incassata: "text-emerald-600 bg-emerald-400/10 border-emerald-400/30",
};

export default function FinanzaAgente() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/finanza/personale").then((r) => setD(r.data)).catch(() => setD({})); }, []);
  if (!d) return <div className="space-y-4"><div className="skeleton h-10 w-64 rounded-xl" /><div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div></div>;

  const pct = (v, t) => (t ? Math.min(100, Math.round((v / t) * 100)) : 0);

  return (
    <div className="fade-up" data-testid="finanza-agente-page">
      <SectionTitle sub="La tua situazione economica personale: provvigioni e produzione. Sezione riservata al tuo account.">Finanza</SectionTitle>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4 mb-4">
        <KpiCard testid="fa-previste" label="Provvigioni previste" value={euro(d.provvigioni_previste || 0)} icon={Clock} />
        <KpiCard testid="fa-maturate" label="Provvigioni maturate" value={euro(d.provvigioni_maturate || 0)} icon={TrendingUp} accent />
        <KpiCard testid="fa-fatturate" label="Provvigioni fatturate" value={euro(d.provvigioni_fatturate || 0)} icon={FileText} />
        <KpiCard testid="fa-incassate" label="Provvigioni incassate" value={euro(d.provvigioni_incassate || 0)} icon={CheckCircle2} accent />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <div className="card-immo p-5" data-testid="fa-prod-mese">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)] mb-2">Produzione mensile</div>
          <div className="flex items-end justify-between mb-2">
            <div className="mono text-2xl font-extrabold">{euro(d.produzione_mese || 0)}</div>
            <div className="text-xs text-[var(--muted)]">Obiettivo {euro(d.obiettivo_mese || 0)}</div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#FC82ED]" style={{ width: `${pct(d.produzione_mese, d.obiettivo_mese)}%` }} /></div>
        </div>
        <div className="card-immo p-5" data-testid="fa-prod-anno">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--muted)] mb-2">Produzione annuale</div>
          <div className="flex items-end justify-between mb-2">
            <div className="mono text-2xl font-extrabold">{euro(d.produzione_anno || 0)}</div>
            <div className="text-xs text-[var(--muted)]">Obiettivo {euro(d.obiettivo_anno || 0)}</div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#FC82ED]" style={{ width: `${pct(d.produzione_anno, d.obiettivo_anno)}%` }} /></div>
        </div>
      </div>

      <SectionTitle>Operazioni collegate</SectionTitle>
      <div className="space-y-2 mt-2">
        {(!d.operazioni || d.operazioni.length === 0) && <div className="card-immo p-6 text-center text-[var(--muted)] text-sm">Nessuna operazione registrata.</div>}
        {(d.operazioni || []).map((o) => (
          <div key={o.id} data-testid={`fa-op-${o.id}`} className="card-immo p-4">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-lg bg-[#FC82ED]/10 text-[#FC82ED] flex items-center justify-center shrink-0"><Wallet size={17} /></div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{o.immobile_titolo || "Operazione"}</div>
                <div className="text-xs text-[var(--muted)] capitalize">Lato {o.lato || "—"}{o.data_incasso ? ` · incasso ${new Date(o.data_incasso).toLocaleDateString("it-IT",{timeZone:"Europe/Rome"})}` : ""}</div>
              </div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border shrink-0 ${STATO_CLS[o.stato] || STATO_CLS.prevista}`}>{STATO_LABEL[o.stato] || o.stato}</span>
            </div>
            <div className="mt-3 pt-3 border-t border-[var(--border)] grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
              <Brk l="Provv. lorda" v={o.imponibile ?? o.importo} />
              <Brk l="IVA" v={o.iva} />
              <Brk l="Quota Immobilart" v={o.quota_agenzia} />
              <Brk l="Quota agente" v={o.quota_agente} />
              <Brk l="Netto spettante" v={o.netto_spettante ?? o.quota_agente} accent />
            </div>
          </div>
        ))}
      </div>

      {Array.isArray(d.storico) && d.storico.length > 0 && (
        <>
          <SectionTitle>Storico economico personale</SectionTitle>
          <div className="card-immo p-4 mt-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {d.storico.slice(-8).map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-[10px] uppercase text-[var(--muted)]">{s.mese}</div>
                  <div className="mono text-sm font-bold">{euro(s.fatturato || 0)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <LeMieProvvigioni />
    </div>
  );
}

function LeMieProvvigioni() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/direzione/le-mie-provvigioni").then((r) => setD(r.data)).catch(() => setD({ voci: [], totali: { da_pagare: 0, pagato: 0 } })); }, []);
  if (!d || (!d.voci?.length && !d.totali?.pagato)) return null;
  const RL = { acquisitore: "Acquisitore", venditore: "Venditore", direttore: "Direttore team", segnalatore: "Segnalatore" };
  return (
    <div className="mt-8" data-testid="le-mie-provvigioni">
      <SectionTitle sub="Quanto ti spetta dalle pratiche incassate dall'agenzia. Vedi solo le tue provvigioni.">Le mie provvigioni</SectionTitle>
      <div className="text-sm text-[var(--muted)] mt-1 mb-2">Da ricevere: <b className="text-[#111111]">{euro(d.totali.da_pagare)}</b> · Ricevuto: {euro(d.totali.pagato)}</div>
      <div className="space-y-2">
        {d.voci.map((v) => (
          <div key={v.id} data-testid={`mia-provv-${v.id}`} className="card-immo p-4 flex items-center justify-between gap-2 flex-wrap">
            <div><div className="text-sm font-semibold">{v.pratica || "Pratica"} · {RL[v.ruolo] || v.ruolo} ({pctIt(v.pct)})</div>
              <div className="text-xs text-[var(--muted)]">{euro(v.importo)}{v.data_pagamento ? ` · pagata ${v.data_pagamento}` : ""}</div></div>
            <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full border ${v.stato === "pagato" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-amber-50 text-amber-600 border-amber-200"}`}>{v.stato === "pagato" ? "Pagata" : v.stato === "attesa_incasso" ? "In attesa d'incasso" : "Da ricevere"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
